import { useState, useSyncExternalStore } from 'react';
import { Box, CircularProgress, Drawer, Fab, IconButton, Paper, Stack, TextField, Typography } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import MuiMarkdown from 'mui-markdown';

import { hasBearerPermission, PERM_USE_CHAT, subscribeToBearerToken } from '../grampsAuth';
import { useChatConversation } from '../hooks/useChatConversation';

export function ChatAssistant({ treeName }: { treeName: string | null }) {
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState('');
    // Reactive so the FAB appears/disappears right away if a silent token refresh
    // (or a fresh login) changes whether the current tree grants chat access,
    // instead of requiring a full page reload to notice.
    const canUseChat = useSyncExternalStore(subscribeToBearerToken, () => hasBearerPermission(PERM_USE_CHAT, treeName));
    const { turns, pending, send } = useChatConversation(treeName);

    if (!canUseChat) {
        return null;
    }

    const handleSend = () => {
        const query = input.trim();
        if (!query || pending) return;
        setInput('');
        void send(query);
    };

    return <>
        {/* Hidden while the drawer is open: at full width its own footer (the send
            button) sits in this same bottom-right corner, and the fixed-position Fab
            would otherwise float on top of it and swallow its clicks. */}
        {!open && <Fab
            color="primary"
            aria-label="Assistant IA"
            onClick={() => setOpen(true)}
            sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: (theme) => theme.zIndex.drawer + 2 }}
        >
            <ChatIcon />
        </Fab>}
        <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
            <Box sx={{ width: { xs: '100vw', sm: 380 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="h6">Assistant IA</Typography>
                    <IconButton onClick={() => setOpen(false)} aria-label="Fermer"><CloseIcon /></IconButton>
                </Stack>
                <Stack spacing={1.5} sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
                    {turns.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            Posez une question sur votre arbre généalogique.
                        </Typography>
                    )}
                    {turns.map((turn, index) => (
                        <Paper
                            key={index}
                            elevation={0}
                            sx={{
                                alignSelf: turn.role === 'user' ? 'flex-end' : 'flex-start',
                                bgcolor: turn.role === 'user' ? 'primary.main' : turn.role === 'error' ? 'error.light' : 'grey.100',
                                color: turn.role === 'user' ? 'primary.contrastText' : 'text.primary',
                                px: 1.5,
                                py: 1,
                                borderRadius: 2,
                                maxWidth: '90%',
                            }}
                        >
                            {turn.role === 'assistant'
                                ? <MuiMarkdown>{turn.content}</MuiMarkdown>
                                : <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{turn.content}</Typography>}
                        </Paper>
                    ))}
                    {pending && (
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', alignSelf: 'flex-start' }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2" color="text.secondary">Réflexion en cours...</Typography>
                        </Stack>
                    )}
                </Stack>
                <Stack direction="row" spacing={1} sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Votre question..."
                        value={input}
                        disabled={pending}
                        onChange={(event) => setInput(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault();
                                handleSend();
                            }
                        }}
                    />
                    <IconButton color="primary" aria-label="Envoyer" onClick={handleSend} disabled={pending || !input.trim()}>
                        <SendIcon />
                    </IconButton>
                </Stack>
            </Box>
        </Drawer>
    </>;
}
